import { faker } from "@faker-js/faker/locale/en_US";
import moment from "moment";
import { Moment } from "moment";
import { IBasicReservation, IReservation } from "services/models";
import { toCurrencyFormat } from "utils";
import { v4 as uuidv4 } from "uuid";
import { getRandom, getRandomArray, getRandomDate, getRandomDecimal, getRandomInt } from "./data";
import { ILinedItemCharges } from "components/Player/Booking/Summary";

function buildLineItems(checkIn: Moment, numberOfDays: number, subtotal: number, total: number) {
  const lineItems = faker.datatype.boolean() ? null : ([] as ILinedItemCharges[]);

  if (lineItems === null) return lineItems;

  for (var i = 0; i < numberOfDays; i++) {
    const date = checkIn.clone().add(i, "days");
    lineItems.push({
      title: date.format("MMM DD"),
      cost: {
        value: toCurrencyFormat(subtotal / numberOfDays),
      },
    });
  }
  return [
    lineItems,
    [
      {
        title: "Sub-Total",
        cost: {
          value: toCurrencyFormat(subtotal),
        },
      },
      {
        title: "Taxes & Fees",
        cost: {
          value: toCurrencyFormat(subtotal * 0.1),
        },
      },
    ],
    [
      {
        title: "Total For Stay",
        cost: {
          value: toCurrencyFormat(total),
        },
      },
    ],
  ];
}

function generateReservations() {
  return getRandomArray(0, 60).map(() => {
    const status = getRandom(["upcoming", "cancelled", "previous", "current"]);
    const startDate = getRandomDate(2020, moment().year());

    let checkIn = startDate.clone().add(16, "hours");
    const numberOfDays = getRandomInt(1, 4);
    let checkOut = checkIn.clone().add(numberOfDays, "days").add(11, "hours");
    if (status !== "upcoming") {
      checkIn = checkIn.add(getRandomDecimal(-2, 2), "hours");
      checkOut = checkOut.add(getRandomDecimal(-2, 2), "hours");
    }

    const firstName = faker.person.firstName();
    const lastName = faker.person.lastName();

    const subTotalCost = getRandom([true, false, false, false])
      ? 0
      : getRandomInt(100 * numberOfDays, 400 * numberOfDays);
    const totalCost = subTotalCost + subTotalCost * 0.1;
    const averagePerNight = totalCost / numberOfDays;

    const checkInDateDisplay = checkIn.format("ddd, MMM DD");
    const checkOutDateDisplay = checkOut.format("ddd, MMM DD");

    const confirmationNumber = getRandomInt(23293599, 33293599) + "-1";
    return {
      id: confirmationNumber,
      bookedUsing: getRandom(["Yaamava Android", "Yaamava IOS"]),
      confirmationNumber,
      checkIn: checkIn,
      checkOut: checkOut,
      checkInDisplay: {
        date: checkInDateDisplay,
        time: checkIn.format("h:mm a"),
      },
      checkoutDisplay: {
        date: checkOutDateDisplay,
        time: checkOut.format("h:mm a"),
      },
      displayText: `${checkInDateDisplay} - ${checkOutDateDisplay} (${numberOfDays - 1} Night)`,
      roomImage: "https://uxmstagingmediacdn.azureedge.net/hotel/KingRoom/KingRoom_Thrillist-1_Resized640x426.jpg",
      nights: numberOfDays - 1,
      roomTypeText: "Standard King Room (2 Guests Max)",
      status,
      totalCost: toCurrencyFormat(totalCost),
      perNightValue: toCurrencyFormat(averagePerNight),
      guests: getRandomArray(1, 2).map((index) => ({
        guestId: uuidv4(),
        firstName,
        lastName,
        clubNumber: faker.number.int(),
        email: faker.internet.email(),
        phoneNumber: faker.phone.number(),
        primary: index === 1,
      })),
      estimatedArrival: null,
      notes: getRandom([true, false]) ? faker.lorem.lines() : null,
      preferences: getRandom([true, false])
        ? [
            {
              code: "CNN",
              name: "Cornering Room",
            },
          ]
        : [],
      payment: getRandom([true, false, true])
        ? {
            shift4Token: "...",
            cardMask: "1234",
            expMonth: "02",
            expYear: "24",
            name: `${firstName} ${lastName}`,
            cardType: getRandom(["MC", "VS", "AX", "NS", "OTHER"]),
          }
        : null,
      lineItems: buildLineItems(checkIn, numberOfDays, subTotalCost, totalCost),
    };
  });
}
class MockReservationsDataStore {
  reservations = generateReservations();

  getByStatus(status: "current" | "upcoming" | "cancelled" | "previous"): IBasicReservation[] {
    return this.reservations.filter((r) => r.status === status);
  }

  getBy(confirmationNumber: string): IReservation | undefined {
    return this.reservations.find((r) => r.confirmationNumber === confirmationNumber);
  }
  cancelReservation = (confirmationNumber: string | number) => {
    this.reservations = this.reservations.map((r) => {
      if (r.id === confirmationNumber) {
        r.status = "cancelled";
      }
      return r;
    });
  };
}

export const mockReservations = new MockReservationsDataStore();
